import _extends from "@babel/runtime/helpers/extends";
import React, { memo, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { useTheme, createUseStyles, Section, Text, Modal, Icon } from 'hoi-poi-ui';
import { useWeb5Context } from "../../lib/Web5Context";
import CarouselFullModal from "../../components/CarouselFullModal";
import Image from "./Image";
import ModalHeaderLeft from "./ModalHeaderLeft";
import ModalHeaderCenter from "./ModalHeaderCenter";
import ModalHeaderRight from "./ModalHeaderRight";
import styles from "./styles";
const useStyles = createUseStyles(styles, 'UploadImageCarousel');
const UploadImageCarousel = _ref => {
  let {
    limit,
    onDelete,
    onAdd,
    carouselFullModalClass,
    images = []
  } = _ref;
  const classes = useStyles();
  const theme = useTheme();
  const {
    t
  } = useWeb5Context();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [deleteImage, setDeleteImage] = useState(null);
  const handleDelete = useCallback(imageId => {
    setDeleteImage(imageId);
  }, []);
  const handleConfirmDelete = useCallback(() => {
    onDelete(deleteImage);
    setDeleteImage(null);
  }, [deleteImage, onDelete]);
  const handleCancelDelete = useCallback(() => {
    setDeleteImage(null);
  }, []);
  const renderContentLeft = useCallback(() => /*#__PURE__*/React.createElement(ModalHeaderLeft, {
    name: images[currentImage] && images[currentImage].name || null
  }), [currentImage, images]);
  const renderContentCenter = useCallback(() => /*#__PURE__*/React.createElement(ModalHeaderCenter, {
    index: currentImage,
    totalImages: images && images.length || 0
  }), [currentImage, images]);
  var _ref2 = /*#__PURE__*/React.createElement(ModalHeaderRight, {
    index: currentImage,
    onDelete: handleDelete
  });
  const renderContentRight = useCallback(() => _ref2, [currentImage, handleDelete]);
  const onDrop = useCallback(acceptedFiles => {
    if (!acceptedFiles) return;
    const imagesLength = Object.keys(images).length;
    if (acceptedFiles.length > limit - imagesLength) {
      //Toast
      console.warn(`You can't upload more than ${limit - imagesLength} images.`);
      return;
    }
    const validFiles = acceptedFiles.filter(file => {
      if (file.type !== 'image/jpeg' && file.type !== 'image/png' && file.type !== 'image/gif') {
        //Toast
        console.warn('File type not supported');
        return false;
      } else if (file.size > 16777216) {
        //Toast
        console.warn('File size error');
        return false;
      } else {
        return true;
      }
    });
    onAdd(validFiles);
  }, [images, limit, onAdd]);
  const {
    getRootProps,
    getInputProps,
    isDragActive
  } = useDropzone({
    onDrop
  });
  const updateImageSelected = useCallback(index => {
    setCurrentImage(index);
  }, [setCurrentImage]);
  const openViewImage = useCallback(index => {
    setCurrentImage(index);
    setModalIsOpen(true);
  }, []);
  const closeViewImage = useCallback(() => {
    setModalIsOpen(false);
  }, []);
  const imagesList = useMemo(() => {
    if (!images) return null;
    return images.map((el, i) => /*#__PURE__*/React.createElement(Image, {
      key: el.id,
      img: el,
      index: i,
      onDelete: handleDelete,
      onView: openViewImage
    }));
  }, [openViewImage, images, handleDelete]);
  const showDropZone = useMemo(() => images && images.length < limit, [images, limit]);
  const modalOverrides = useMemo(() => ({
    confirmButton: {
      color: 'danger'
    }
  }), []);
  return /*#__PURE__*/React.createElement("div", {
    className: classes.root
  }, /*#__PURE__*/React.createElement(Section, {
    title: t('label_photo_gallery'),
    className: classes.section
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.container
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.carousel
  }, imagesList), showDropZone && /*#__PURE__*/React.createElement("div", _extends({
    className: classes.dropZone
  }, getRootProps()), /*#__PURE__*/React.createElement("input", getInputProps()), isDragActive ? /*#__PURE__*/React.createElement("div", {
    className: classes.dropZoneActive
  }, /*#__PURE__*/React.createElement(Text, {
    className: classes.dropText
  }, t('cfm_label_drop_to_upload'))) : /*#__PURE__*/React.createElement("div", {
    className: classes.dropZoneInactive
  }, /*#__PURE__*/React.createElement(Icon, {
    name: "cloudUpload",
    className: classes.cloudUploadIcon,
    color: theme.colors.utility.textSecondary
  }), /*#__PURE__*/React.createElement(Text, {
    className: classes.dropText
  }, t('cfm_label_drop_to_upload')))))), /*#__PURE__*/React.createElement(CarouselFullModal, {
    isOpen: modalIsOpen || false,
    withCloseIcon: true,
    onRequestClose: closeViewImage,
    updateImageSelected: updateImageSelected,
    contentLeft: renderContentLeft,
    contentCenter: renderContentCenter,
    contentRight: renderContentRight,
    images: images,
    imageSelected: currentImage !== null ? currentImage : 0,
    className: carouselFullModalClass
  }), /*#__PURE__*/React.createElement(Modal, {
    title: t('title_warning'),
    isOpen: deleteImage !== null,
    size: "small",
    onRequestClose: handleCancelDelete,
    onCancel: handleCancelDelete,
    onConfirm: handleConfirmDelete,
    cancelText: t('cfm_action_yes'),
    confirmText: t('cfm_action_no'),
    overlayClassName: classes.deleteModalOverlay,
    overrides: modalOverrides
  }, /*#__PURE__*/React.createElement(Text, null, t('label_confirm_delete_image'))));
};
process.env.NODE_ENV !== "production" ? UploadImageCarousel.propTypes = {
  limit: PropTypes.number.isRequired,
  images: PropTypes.array.isRequired
} : void 0;
export default /*#__PURE__*/memo(UploadImageCarousel);